import React, { useEffect, useState } from "react";
import { Download, Smartphone, X } from "lucide-react";

// Shows an "Install app" button when the browser offers PWA installation.
export const InstallPWA = () => {
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setDeferred(null));
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (!deferred) return null;

  const install = async () => {
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  };

  return (
    <button data-testid="pwa-install" onClick={install}
      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-accent text-secondary font-semibold hover:bg-accent/90 transition-colors">
      <Download className="h-3.5 w-3.5" /> <span className="hidden xs:inline sm:inline">Install app</span>
    </button>
  );
};

const NUDGE_DISMISS_KEY = "ud_install_nudge_dismissed";

// A gentle, dismissible "Add to Home Screen" banner shown only to alumni on mobile
// browsers that aren't already running the installed app.
export const InstallNudge = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
    const dismissed = localStorage.getItem(NUDGE_DISMISS_KEY) === "1";
    if (isStandalone || dismissed) return;

    const isIOS = /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
    const handler = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", handler);
    const onInstalled = () => { setShow(false); localStorage.setItem(NUDGE_DISMISS_KEY, "1"); };
    window.addEventListener("appinstalled", onInstalled);
    // iOS Safari never fires beforeinstallprompt — show a guided hint instead.
    let t;
    if (isIOS) t = setTimeout(() => setShow(true), 1200);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  if (!show) return null;

  const dismiss = () => { setShow(false); localStorage.setItem(NUDGE_DISMISS_KEY, "1"); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    dismiss();
  };

  return (
    <div className="md:hidden fixed inset-x-0 bottom-0 z-40 px-3 pt-3" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }} data-testid="install-nudge">
      <div className="rounded-xl bg-secondary text-white shadow-[0_-8px_40px_-12px_rgba(0,0,0,0.5)] border border-white/10 px-3.5 py-3 flex items-center gap-3">
        <span className="h-9 w-9 rounded-lg bg-accent/20 text-accent flex items-center justify-center shrink-0"><Smartphone className="h-4.5 w-4.5" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold leading-tight">Add to Home Screen</div>
          <div className="text-xs text-white/70 leading-snug mt-0.5">
            {deferred ? "Install the app for a faster experience." : "Tap Share, then \u201cAdd to Home Screen\u201d for a faster experience."}
          </div>
        </div>
        {deferred && (
          <button data-testid="install-nudge-add" onClick={install} className="shrink-0 flex items-center gap-1.5 text-xs px-3 py-2 rounded-full bg-accent text-secondary font-semibold hover:bg-accent/90 transition-colors">
            <Download className="h-3.5 w-3.5" /> Add
          </button>
        )}
        <button data-testid="install-nudge-dismiss" onClick={dismiss} aria-label="Dismiss" className="shrink-0 text-white/60 hover:text-white p-1"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
};
